// canvas.tsx
import React, { useRef, useState } from "react";
import { ActionNode, TriggerNode } from "../../../views/convoauto-pages/bot-studio/types";
import { TriggerNodeCard } from "./trigger-node";
import { RcsActionSummaryCard } from "./action/rcs-action-summary";
import { TemplateModule } from "../../../views/marketing-pages/templates";

type MediaUploadRequest = (data: FormData) => Promise<any[]>;

// Node types that fork into two branches via config.true_next/false_next —
// "Condition" is the original; "Wait for Reply" and "Branch" (RCS-only) reuse
// the exact same mechanism, just with different branch labels.
const BRANCH_LABELS: Record<string, [string, string]> = {
  Condition: ["True", "False"],
  "Wait for Reply": ["Chip Reply", "Default"],
  Branch: ["Match", "No Match"],
};

interface CanvasProps {
  triggerNode: TriggerNode | null;
  actionNodes: ActionNode[];
  onDropTrigger: (type: string) => void;
  onDropAction: (type: string) => void;
  onUpdateTrigger: (n: TriggerNode) => void;
  onDeleteTrigger?: () => void;
  onResetTrigger?: () => void;
  onRemoveAction?: (id: string) => void;
  onUpdateAction?: (id: string, config: Record<string, any>) => void;
  templates?: TemplateModule[];
  mediaUpload?: MediaUploadRequest;
  broadcastExcelUpload?: MediaUploadRequest;
  journeyId?: string | null;
  // Action nodes render as compact cards that open the config panel on
  // click instead of editing inline on the canvas.
  selectedActionId?: string | null;
  onSelectAction?: (id: string) => void;
}

// ─── Arrow SVG between nodes ──────────────────────────────────────────────────
const HorizontalArrow = () => (
  <svg width="80" height="40" className="flex-shrink-0" style={{ marginTop: "auto", marginBottom: "auto" }}>
    <defs>
      <marker id="arrowhead" markerWidth="8" markerHeight="8" refX="7" refY="4" orient="auto">
        <path d="M 0 0 L 8 4 L 0 8 z" fill="#6366f1" />
      </marker>
    </defs>
    <line x1="0" y1="20" x2="72" y2="20" stroke="#6366f1" strokeWidth="2" markerEnd="url(#arrowhead)" />
  </svg>
);

// ─── Recursive branch renderer ────────────────────────────────────────────────
/**
 * Renders a node and, if it's a Condition, forks into two vertical branches.
 * `visitedIds` prevents infinite loops if the graph has cycles.
 */
const FlowNode: React.FC<{
  nodeId: string;
  nodeMap: Map<string, ActionNode>;
  visitedIds: Set<string>;
  onRemoveAction: (id: string) => void;
  onUpdateAction: (id: string, config: Record<string, any>) => void;
  templates?: TemplateModule[];
  mediaUpload?: MediaUploadRequest;
  broadcastExcelUpload?: MediaUploadRequest;
  journeyId?: string | null;
  isFirst?: boolean;
  triggerEventType?: string;
  selectedActionId?: string | null;
  onSelectAction?: (id: string) => void;
}> = ({
  nodeId,
  nodeMap,
  visitedIds,
  onRemoveAction,
  onUpdateAction,
  templates,
  mediaUpload,
  broadcastExcelUpload,
  journeyId,
  isFirst = false,
  triggerEventType,
  selectedActionId,
  onSelectAction,
}) => {
  const node = nodeMap.get(nodeId);

  // ── Sync downstream Send/Send-after cards ────────────────────────────────
  // Must run on EVERY render (rules-of-hooks) — placed before any early
  // return. Effect body no-ops when the node isn't a WhatsApp condition
  // or when required IDs are missing, so it's safe to always attach.
  // Persists template picks from the Condition side-panel onto the two
  // downstream WhatsApp Message cards (source of truth on the WhatsApp
  // path — Joseph 2026-08-04 "auto selected depending what the user
  // picks in condition").
  const syncIsWhatsAppCondition =
    !!node && node.type === "Condition" && node.config?.channel === "WhatsApp";
  const syncActions: any[] = syncIsWhatsAppCondition && Array.isArray(node?.config?.whatsapp_actions)
    ? node!.config!.whatsapp_actions
    : [];
  const syncSorted = [...syncActions].sort(
    (a, b) => (Number(a?.delay_seconds) || 0) - (Number(b?.delay_seconds) || 0)
  );
  const syncSendTemplate      = String(syncSorted[0]?.template || "");
  const syncSendAfterTemplate = String(syncSorted[1]?.template || "");
  const syncTrueId  = String(node?.config?.true_next  || "");
  const syncFalseId = String(node?.config?.false_next || "");
  // After the visual swap below, trueNode holds the immediate (Send) row
  // and falseNode the delayed (Send after). The swap key: if the delayed
  // template was originally wired into the True slot, we flip which id
  // gets which template. Recompute that here for the effect dep array.
  const syncTrueTplNow  = String(nodeMap.get(syncTrueId )?.config?.templateName || nodeMap.get(syncTrueId )?.config?.template || "");
  const syncFalseTplNow = String(nodeMap.get(syncFalseId)?.config?.templateName || nodeMap.get(syncFalseId)?.config?.template || "");
  const syncSwapped = !!(syncSendTemplate && syncFalseTplNow === syncSendTemplate && syncTrueTplNow !== syncSendTemplate);
  const syncSendId      = syncSwapped ? syncFalseId : syncTrueId;
  const syncSendAfterId = syncSwapped ? syncTrueId  : syncFalseId;
  const syncSendTplNow      = syncSwapped ? syncFalseTplNow : syncTrueTplNow;
  const syncSendAfterTplNow = syncSwapped ? syncTrueTplNow  : syncFalseTplNow;
  React.useEffect(() => {
    if (!syncIsWhatsAppCondition) return;
    if (syncSendId && syncSendTemplate && syncSendTplNow !== syncSendTemplate) {
      const nodeNow = nodeMap.get(syncSendId);
      onUpdateAction(syncSendId, {
        ...(nodeNow?.config || {}),
        templateName: syncSendTemplate,
        template:     syncSendTemplate,
      });
    }
    if (syncSendAfterId && syncSendAfterTemplate && syncSendAfterTplNow !== syncSendAfterTemplate) {
      const nodeNow = nodeMap.get(syncSendAfterId);
      onUpdateAction(syncSendAfterId, {
        ...(nodeNow?.config || {}),
        templateName: syncSendAfterTemplate,
        template:     syncSendAfterTemplate,
      });
    }
  }, [
    syncIsWhatsAppCondition,
    syncSendTemplate,
    syncSendAfterTemplate,
    syncSendId,
    syncSendAfterId,
    syncSendTplNow,
    syncSendAfterTplNow,
  ]);

  if (!node || visitedIds.has(nodeId)) return null;

  const newVisited = new Set(visitedIds).add(nodeId);
  // Condition arrow labels — Email defaults to True/False. WhatsApp
  // conditions read as Send / Send after — the immediate row lands on
  // "Send", the delayed row on "Send after". Template names are not
  // concatenated here (Joseph 2026-08-04 "not send vouch…"); the
  // template picker inside the condition card is where the operator
  // sees which template maps to which row.
  const isWhatsAppCondition =
    node.type === "Condition" && node.config?.channel === "WhatsApp";
  const branchLabels = isWhatsAppCondition
    ? (["Send", "Send after"] as [string, string])
    : BRANCH_LABELS[node.type];
  const isCondition = !!branchLabels;
  const [trueLabel, falseLabel] = branchLabels ?? ["True", "False"];
  const trueNextId: string = node.config?.true_next ?? "";
  const falseNextId: string = node.config?.false_next ?? "";

  let trueNode = trueNextId ? nodeMap.get(trueNextId) : null;
  let falseNode = falseNextId ? nodeMap.get(falseNextId) : null;

  // WhatsApp condition — auto-swap the visual assignment so the immediate
  // (0h) template always sits under "Send" and the delayed under "Send
  // after", regardless of which slot (true_next vs false_next) the
  // operator originally dropped the downstream node into. Same swap key
  // that syncSwapped above uses so render matches what the sync effect
  // considers the "Send" slot.
  if (isWhatsAppCondition && syncSwapped) {
    const swap = trueNode;
    trueNode = falseNode;
    falseNode = swap;
  }

  // ── Non-condition: render card + arrow + next node inline ──────────────────
  if (!isCondition) {
    // Find what comes after this node (next in sequence that isn't a branch target)
    // For linear nodes we just look at the next sequential step
    return (
      <div className="flex items-center">
        {!isFirst && <HorizontalArrow />}
        <RcsActionSummaryCard
          node={node}
          selected={selectedActionId === node.id}
          onSelect={() => onSelectAction?.(node.id)}
          onUpdate={(config) => onUpdateAction(node.id, config)}
        />
      </div>
    );
  }

  // ── Condition node: render card + forked True/False branches ──────────────
  return (
    <div className="flex items-center">
      {!isFirst && <HorizontalArrow />}

      {/* The condition card itself */}
      <RcsActionSummaryCard
        node={node}
        selected={selectedActionId === node.id}
        onSelect={() => onSelectAction?.(node.id)}
        onUpdate={(config) => onUpdateAction(node.id, config)}
      />

      {/* Fork: two vertical branches.
          Each row owns its own horizontal arrow so the True / False label
          always points DIRECTLY at the card it labels, no matter how tall
          the cards grow. The vertical dashed spine connects the two arrows
          via flex-fill so it spans whatever vertical gap exists.
          (The previous fixed-height SVG made the False arrow appear floating
          in empty space above the cold card when cards grew past 160px.) */}
      {(trueNode || falseNode) && (
        <div className="flex items-stretch ml-0 gap-3">
          {/* Arrows column — True row, spine, False row stacked vertically */}
          <div className="flex flex-col justify-between flex-shrink-0 w-[60px]">
            {/* True arrow row */}
            <div className="flex items-center gap-1">
              <span className="text-[10px] font-semibold text-indigo-600 select-none">{trueLabel}</span>
              <div className="flex-1 h-0.5 bg-indigo-500 relative">
                <span className="absolute right-0 top-1/2 -translate-y-1/2 w-0 h-0 border-l-[6px] border-l-indigo-500 border-y-[4px] border-y-transparent" />
              </div>
            </div>
            {/* Dashed spine — flex-fills to span the gap between True + False rows */}
            <div className="flex-1 border-l-2 border-dashed border-indigo-300 ml-2 my-1" />
            {/* False arrow row */}
            <div className="flex items-center gap-1">
              <span className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 select-none">{falseLabel}</span>
              <div className="flex-1 h-0.5 bg-gray-400 dark:bg-gray-600 relative">
                <span className="absolute right-0 top-1/2 -translate-y-1/2 w-0 h-0 border-l-[6px] border-l-gray-400 border-y-[4px] border-y-transparent" />
              </div>
            </div>
          </div>

          {/* Branch cards stacked vertically */}
          <div className="flex flex-col gap-16">
            {/* True branch — TOP */}
            <div className="flex items-center">
              {trueNode ? (
                <FlowNode
                  nodeId={trueNode.id}
                  nodeMap={nodeMap}
                  visitedIds={newVisited}
                  onRemoveAction={onRemoveAction}
                  onUpdateAction={onUpdateAction}
                  templates={templates}
                  mediaUpload={mediaUpload}
                  broadcastExcelUpload={broadcastExcelUpload}
                  journeyId={journeyId}
                  isFirst={true}
                  triggerEventType={triggerEventType}
                  selectedActionId={selectedActionId}
                  onSelectAction={onSelectAction}
                />
              ) : (
                <EmptyBranchSlot label={trueLabel} />
              )}
            </div>

            {/* False branch — BOTTOM */}
            <div className="flex items-center">
              {falseNode ? (
                <FlowNode
                  nodeId={falseNode.id}
                  nodeMap={nodeMap}
                  visitedIds={newVisited}
                  onRemoveAction={onRemoveAction}
                  onUpdateAction={onUpdateAction}
                  templates={templates}
                  mediaUpload={mediaUpload}
                  broadcastExcelUpload={broadcastExcelUpload}
                  journeyId={journeyId}
                  isFirst={true}
                  triggerEventType={triggerEventType}
                  selectedActionId={selectedActionId}
                  onSelectAction={onSelectAction}
                />
              ) : (
                <EmptyBranchSlot label={falseLabel} />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Empty branch slot (shows when a branch has no card yet) ─────────────────
const EmptyBranchSlot: React.FC<{ label: string }> = ({ label }) => (
  <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl px-4 py-3 text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 w-[200px] text-center select-none">
    Drop action for <span className="font-semibold text-gray-500 dark:text-gray-400">{label}</span> path
  </div>
);

// ─── Build ordered linear chain before first condition ────────────────────────
/**
 * Determines which nodes are "branch targets" (reachable only via true_next/false_next
 * of a Condition). Those are excluded from the top-level linear chain.
 */
function getBranchTargetIds(nodes: ActionNode[]): Set<string> {
  const targets = new Set<string>();
  for (const node of nodes) {
    if (BRANCH_LABELS[node.type]) {
      if (node.config?.true_next) targets.add(node.config.true_next);
      if (node.config?.false_next) targets.add(node.config.false_next);
    }
  }
  return targets;
}

export const Canvas: React.FC<CanvasProps> = ({
  triggerNode,
  actionNodes,
  onDropTrigger,
  onDropAction,
  onUpdateTrigger,
  onDeleteTrigger = () => {},
  onResetTrigger = () => {},
  onRemoveAction = () => {},
  onUpdateAction = () => {},
  templates,
  mediaUpload,
  broadcastExcelUpload,
  journeyId,
  selectedActionId,
  onSelectAction,
}) => {
  const canvasRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  const handleDragOver = (e: React.DragEvent) => e.preventDefault();
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const data = e.dataTransfer.getData("text/plain");
    if (!data) return;
    const { type, category } = JSON.parse(data);
    if (category === "trigger") onDropTrigger(type);
    else onDropAction(type);
  };

  const zoomIn  = () => setScale((p) => Math.min(p + 0.1, 2));
  const zoomOut = () => setScale((p) => Math.max(p - 0.1, 0.4));

  // Build a map for O(1) lookup
  const nodeMap = new Map<string, ActionNode>(actionNodes.map((n) => [n.id, n]));

  // Nodes that are only reachable via condition branches — not in the top-level chain
  const branchTargets = getBranchTargetIds(actionNodes);

  // Top-level linear chain: nodes that are NOT branch targets
  const topLevelNodes = actionNodes.filter((n) => !branchTargets.has(n.id));

  return (
    <div
      ref={canvasRef}
      className="flex-1 relative bg-gray-50 dark:bg-transparent overflow-auto"
      style={{
        backgroundImage: "radial-gradient(circle, #d1d5db 1px, transparent 1px)",
        backgroundSize: "24px 24px",
      }}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {/* ── Zoom controls ── */}
      <div className="fixed bottom-36 right-6 z-50 flex flex-col gap-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg p-1">
        <button
          onClick={zoomIn}
          className="w-10 h-10 flex items-center justify-center hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg text-xl font-bold text-gray-700 dark:text-gray-300"
          title="Zoom In"
        >+</button>
        <div className="w-px mx-auto h-px bg-gray-200 dark:bg-gray-700" />
        <button
          onClick={zoomOut}
          className="w-10 h-10 flex items-center justify-center hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg text-2xl font-bold text-gray-700 dark:text-gray-300"
          title="Zoom Out"
        >−</button>
        <div className="text-center text-[10px] text-[#7B7B7B] font-semibold dark:text-gray-500 py-1 border-t border-gray-200 dark:border-gray-700">
          {Math.round(scale * 100)}%
        </div>
      </div>

      {/* ── Flow canvas ── */}
      <div className="min-w-max min-h-full flex items-start p-16">
        <div
          style={{ transform: `scale(${scale})`, transformOrigin: "top left" }}
          className="flex items-center transition-transform duration-150"
        >
          {/* Trigger */}
          <TriggerNodeCard
            node={triggerNode}
            onChange={onUpdateTrigger}
            onDelete={onDeleteTrigger}
            onReset={onResetTrigger}
            templates={templates}
          />

          {/* Linear top-level chain */}
          {triggerNode && topLevelNodes.map((node, index) => (
            <FlowNode
              key={node.id}
              nodeId={node.id}
              nodeMap={nodeMap}
              visitedIds={new Set()}
              onRemoveAction={onRemoveAction}
              onUpdateAction={onUpdateAction}
              templates={templates}
              mediaUpload={mediaUpload}
              broadcastExcelUpload={broadcastExcelUpload}
              journeyId={journeyId}
              isFirst={false}
              triggerEventType={(triggerNode?.eventType as string) ?? ""}
              selectedActionId={selectedActionId}
              onSelectAction={onSelectAction}
            />
          ))}
        </div>
      </div>
    </div>
  );
};